import { useSensorsQueryV1 } from "@/api/v1/sensor-api";
import { SensorsTable } from "@/components/sensors/table/data-table";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Antenna,
  Shield,
  Zap,
  ExternalLink,
  Activity,
  MapPin,
  CheckCircle,
  AlertTriangle,
  Info,
  Plus,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useFeatureAccess } from "@/hooks/use-subscription-limits";

export const SensorsRoute = () => {
  const { data: sensors, isLoading, error } = useSensorsQueryV1();
  const hasSensors = sensors && sensors.length > 0;
  const activeSensors = sensors?.filter(s => s.status === "active")?.length ?? 0;
  const totalSensors = sensors?.length ?? 0;
  
  const { limit, isUnlimited: isUnlimitedSensors } = useFeatureAccess(
    "sensors",
    totalSensors
  );
  
  const hasReachedLimit = !isUnlimitedSensors && limit > 0 && totalSensors >= limit;

  return (
    <main>
      <div className="container mx-auto p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
        <BreadcrumbNav
          items={[
            { label: "Home", href: "/" },
            { label: "Sensors", href: "/sensors" },
          ]}
          className="mb-6"
        />
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold">Sensors</h1>
            <p className="text-sm sm:text-base text-muted-foreground">
            Manage your Remote ID sensors and monitor drone activity
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild disabled={hasReachedLimit}>
              <Link to="/sensors">
                <Plus className="h-4 w-4 mr-2" />
                Add Sensor
              </Link>
            </Button>
            {hasReachedLimit && (
              <Badge variant="outline" className="w-fit">
                <Info className="h-3 w-3 mr-1" />
                {`${totalSensors}/${limit} Limit Reached`}
              </Badge>
            )}
            {!hasReachedLimit && !isUnlimitedSensors && limit > 0 && (
              <Badge variant="secondary" className="w-fit">
                <Info className="h-3 w-3 mr-1" />
                {totalSensors}/{limit} Used
              </Badge>
            )}
            {!hasSensors && !hasReachedLimit && (
              <Badge variant="secondary" className="w-fit">
                <Zap className="h-3 w-3 mr-1" />
                Hardware Required
              </Badge>
            )}
          </div>
        </div>

        {/* Key Metrics */}
        {hasSensors && (
          <div className="grid gap-3 sm:gap-4 md:gap-6 grid-cols-2 lg:grid-cols-4">
            <Card className="relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full -translate-y-16 translate-x-16" />
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium">Active Sensors</CardTitle>
                <Antenna className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
              </CardHeader>
              <CardContent className="pt-2 sm:pt-0">
                {isLoading ? (
                  <Skeleton className="h-6 sm:h-8 w-12 sm:w-16 mb-1 sm:mb-2" />
                ) : (
                  <div className="text-lg sm:text-2xl font-bold">{activeSensors}</div>
                )}
                <p className="text-xs text-muted-foreground">
                  {isLoading ? <Skeleton className="h-3 sm:h-4 w-16 sm:w-24" /> : `${totalSensors} total sensors`}
                </p>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-full -translate-y-16 translate-x-16" />
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium">Online Status</CardTitle>
                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
              </CardHeader>
              <CardContent className="pt-2 sm:pt-0">
                {isLoading ? (
                  <Skeleton className="h-6 sm:h-8 w-12 sm:w-16 mb-1 sm:mb-2" />
                ) : (
                  <div className="text-lg sm:text-2xl font-bold text-green-600">
                    {sensors?.filter(s => s.status === "active" || s.status === "online").length ?? 0}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {isLoading ? <Skeleton className="h-3 sm:h-4 w-16 sm:w-24" /> : "Currently online"}
                </p>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-full -translate-y-16 translate-x-16" />
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium">Coverage Area</CardTitle>
                <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-orange-600" />
              </CardHeader>
              <CardContent className="pt-2 sm:pt-0">
                {isLoading ? (
                  <Skeleton className="h-6 sm:h-8 w-12 sm:w-16 mb-1 sm:mb-2" />
                ) : (
                  <div className="text-lg sm:text-2xl font-bold">UK</div>
                )}
                <p className="text-xs text-muted-foreground">
                  {isLoading ? <Skeleton className="h-3 sm:h-4 w-16 sm:w-24" /> : "Primary region"}
                </p>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full -translate-y-16 translate-x-16" />
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium">System Health</CardTitle>
                <Activity className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600" />
              </CardHeader>
              <CardContent className="pt-2 sm:pt-0">
                <div className="text-lg sm:text-2xl font-bold text-green-600">Good</div>
                <p className="text-xs text-muted-foreground">
                  All systems operational
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-96" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-center space-x-4">
                      <Skeleton className="h-12 w-12 rounded" />
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-[200px]" />
                        <Skeleton className="h-4 w-[100px]" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Error State */}
        {error && (
          <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p className="font-semibold">Failed to load sensors</p>
                <p>There was an error loading your sensor data. Please try refreshing the page.</p>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Main Content */}
        {!isLoading && !error && (
          <>
            {hasSensors ? (
              <SensorsTable data={sensors ?? []} />
            ) : (
              <Card className="border-2 border-dashed border-muted-foreground/25">
                <CardHeader className="text-center">
                  <div className="mx-auto mb-4 rounded-full bg-gradient-to-r from-primary/20 to-primary/10 p-4">
                    <Antenna className="h-8 w-8 text-primary" />
                  </div>
                  <CardTitle className="text-2xl">Sensor Access Required</CardTitle>
                  <CardDescription className="text-base">
                    You don't have access to sensors. Contact sales to discuss sensor options for your account.
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  <div className="text-center">
                    <Badge variant="outline" className="text-sm">
                      <Shield className="mr-2 h-4 w-4" />
                      Access Required
                    </Badge>
                  </div>

                  <div className="text-center space-y-3">
                    <div className="flex flex-col sm:flex-row gap-2 justify-center">
                      <Button variant="default" asChild>
                        <a href="https://uavity.com/contact" target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Contact Sales
                        </a>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Alternative Features - Only show if no sensors */}
            {!hasSensors && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      Flight Restriction Zones
                    </CardTitle>
                    <CardDescription>
                      Access comprehensive airspace restriction data without additional hardware
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" className="w-full" asChild>
                      <Link to="/flight-restriction-zones">
                        <Shield className="h-4 w-4 mr-2" />
                        Explore Zones
                      </Link>
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="h-5 w-5" />
                      Custom Geofences
                    </CardTitle>
                    <CardDescription>
                      Define and monitor custom airspace boundaries for your specific requirements
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button variant="outline" className="w-full" asChild>
                      <Link to="/geofences">
                        <Zap className="h-4 w-4 mr-2" />
                        Create Geofences
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Help Information */}
            <Alert className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
              <Info className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold">Managing Your Sensors</p>
                  <p>
                    {hasSensors 
                      ? "Monitor your sensor status, view live logs, and manage configurations. Each sensor provides real-time drone detection data for your airspace monitoring needs."
                      : "You don't have access to sensors. Contact hello@uavity.com to discuss sensor options."
                    }
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          </>
        )}
      </div>
    </main>
  );
};
